// Usage beacons (worker/usage.js, read on /admin/usage). Fire and forget: a failure never reaches the page.
// The visitor id is a random id kept in this browser; nothing else about the person is sent.
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
const SESSION_IDLE_MS = 30 * 60 * 1000

// `storage` is a getter: reading window.localStorage itself throws when the browser blocks site data.
function stored(storage, key, make) {
  try {
    const store = storage()
    let value = store.getItem(key)
    if (!value) {
      value = make()
      store.setItem(key, value)
    }
    return value
  } catch {
    return null
  }
}

function visitorId() {
  return typeof window === 'undefined' ? null : stored(() => window.localStorage, 'bb_vid', () => crypto.randomUUID())
}

// A session ends after 30 idle minutes, so a tab left open overnight starts a new one the next day.
function sessionId() {
  if (typeof window === 'undefined') return null
  try {
    const now = Date.now()
    const last = Number(window.sessionStorage.getItem('bb_sid_at') || 0)
    let sid = window.sessionStorage.getItem('bb_sid')
    // A missing or unreadable time (NaN) counts as idle.
    if (!sid || !(now - last <= SESSION_IDLE_MS)) {
      sid = crypto.randomUUID()
      window.sessionStorage.setItem('bb_sid', sid)
    }
    window.sessionStorage.setItem('bb_sid_at', String(now))
    return sid
  } catch {
    return null
  }
}

let lastReferrer = typeof document !== 'undefined' ? document.referrer : ''

/**
 * Count an action on the current page: trackUsage('contact_reveal', { subject: 'waconia-squirt-a' }).
 * `subject` is what it was about (team slug, rink slug, town); other fields are kept as small details.
 */
export function trackUsage(event, { subject, ...detail } = {}) {
  // Everything, storage and the payload included, is inside the catch: tracking runs before a contact reveal or a rink
  // star, and a throw here would stop them.
  try {
    if (typeof window === 'undefined' || navigator.webdriver) return
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) && !import.meta.env.VITE_TRACK_LOCAL) return
    const body = JSON.stringify({
      e: event,
      p: `${window.location.pathname}${window.location.search}`,
      s: subject || undefined,
      d: Object.keys(detail).length ? detail : undefined,
      r: lastReferrer || undefined,
      v: visitorId(),
      sid: sessionId(),
    })
    const url = `${API_BASE}/api/t`
    const blob = new Blob([body], { type: 'text/plain' })
    if (!navigator.sendBeacon?.(url, blob)) {
      fetch(url, { method: 'POST', body, keepalive: true, credentials: 'include' }).catch(() => {})
    }
  } catch {
    // never affects the page
  }
}

/**
 * Mounted once (App): a page view for each new path, and a "filter" event when the same page's query settles on a
 * new value (town, filters), so typing in a box isn't counted letter by letter.
 */
export function UsageTracker() {
  const location = useLocation()
  const lastPath = useRef(null)
  const lastSearch = useRef(null)

  useEffect(() => {
    if (location.pathname !== lastPath.current) {
      trackUsage('page_view')
      // The outside referrer belongs to the first page only.
      lastReferrer = ''
      lastPath.current = location.pathname
      lastSearch.current = location.search
      return undefined
    }
    if (location.search === lastSearch.current) return undefined
    const timer = setTimeout(() => {
      lastSearch.current = location.search
      trackUsage('filter')
    }, 2000)
    return () => clearTimeout(timer)
  }, [location.pathname, location.search])

  return null
}
